import React from 'react';
import { QuickButton } from './QuickButton';
import { FilterIcon, LabelsIcon, ResetIcon } from './icons';
import { FILTER_OPTIONS } from './constants';

/** Filter value -> quick button caption. */
const FILTER_CAPTION = { 'All Notes': 'All notes', 'Triads Only': 'Triads', 'Roots Only': 'Roots' };

/** Label value -> caption, icon text and screen-reader wording. */
const LABEL_META = {
  'Note Names': { caption: 'Names',     icon: 'Aa',  spoken: 'note names' },
  'Intervals':  { caption: 'Intervals', icon: '1·3', spoken: 'intervals' },
  'Fingering':  { caption: 'Fingers',   icon: '1–4', spoken: 'fingering' },
};

/**
 * Filter / Labels / Reset — the quick buttons every instrument puts in InstrumentLayout's
 * `quickButtons` slot.
 *
 * Filter cycles All Notes -> Triads Only -> Roots Only; pass `filterDisabled` where every
 * note always shows (arpeggios, chords). Labels steps through `labelCycle` (Names <-> Intervals
 * by default; fiddle adds Fingering). A stored 'None' counts as the first entry, since None
 * is hidden in the redesign.
 *
 * @param {object} props
 * @param {'sm'|'md'} [props.size]
 * @param {string} props.filter
 * @param {(filter: string) => void} props.onFilterChange
 * @param {boolean} [props.filterDisabled]
 * @param {string} props.label
 * @param {(label: string) => void} props.onLabelChange
 * @param {string[]} [props.labelCycle]
 * @param {() => void} props.onReset
 */
export function StandardQuickButtons({
  size = 'sm',
  filter, onFilterChange, filterDisabled = false,
  label, onLabelChange, labelCycle = ['Note Names', 'Intervals'],
  onReset,
}) {
  const filterCaption = filterDisabled
    ? FILTER_CAPTION['All Notes']
    : (FILTER_CAPTION[filter] ?? FILTER_CAPTION['All Notes']);
  const nextFilter = () => {
    const i = FILTER_OPTIONS.indexOf(filter);
    onFilterChange(FILTER_OPTIONS[(i + 1) % FILTER_OPTIONS.length]);
  };

  const labelIndex = Math.max(0, labelCycle.indexOf(label));
  const current = LABEL_META[labelCycle[labelIndex]];
  const nextLabel = () => onLabelChange(labelCycle[(labelIndex + 1) % labelCycle.length]);

  return (
    <>
      <QuickButton
        size={size}
        icon={<FilterIcon />}
        caption={filterCaption}
        ariaLabel={`Filter: ${filterCaption}`}
        disabled={filterDisabled}
        onClick={nextFilter}
      />
      <QuickButton
        size={size}
        icon={<LabelsIcon text={current.icon} />}
        caption={current.caption}
        ariaLabel={`Labels: ${current.spoken}`}
        onClick={nextLabel}
      />
      <QuickButton size={size} icon={<ResetIcon />} caption="Reset" onClick={onReset} />
    </>
  );
}
